import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  useTheme,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import useTableData from "../../hooks/useTableData";
import getRequiredUserData from "../../utils/getBranchInfo";
import generateCustomID from "../../utils/generateCustomID";
import useWindowDimensions from "../../hooks/useWindowDimensions";
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
} from "firebase/firestore";
import { getOrderTypes } from "../../api/services/orderTypes/orderTypes";

const DataAnalysis = () => {
  const theme = useTheme();
  const branchData = getRequiredUserData();
  let branchId = branchData.requiredId;
  let active = branchData.active;
  let activeSheet = branchData.activeSheet;
  const customID2 = generateCustomID(`${activeSheet}-${branchId}`);
  const { data: tableSummary } = useTableData(customID2);
  const { screenWidth } = useWindowDimensions();
  const avatarSize = screenWidth >= 1536 ? 50 : (screenWidth / 636) * 50 + "px";

  const [topPerformers, setTopPerformers] = useState([]);
  const [bestSellingItems, setBestSellingItems] = useState([]);
  const [bestCustomers, setBestCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customerLoading, setCustomerLoading] = useState(true);

  // Function to get start of this 15 days
  const getStartOf15days = () => {
    const now = new Date();
    const start = new Date(now);
    start.setDate(start.getDate() - 15);
    start.setHours(0, 0, 0, 0);

    // return new Date(now.setFullYear(now.getFullYear() - 1));

    // const day = now.getDay(); // 0 (Sun) - 6 (Sat)
    // const diff = now.getDate() - day + (day === 0 ? -6 : 1); // Monday as first day
    // const start = new Date(now);
    // start.setDate(diff);
    // start.setHours(0, 0, 0, 0);
    return start;
  };

  // Find all delivery personnel with the highest total and fetch their user info
  useEffect(() => {
    const findTopPerformers = async () => {
      if (!tableSummary || tableSummary.length === 0) {
        setTopPerformers([]);
        return;
      }

      // Filter out the "total" entry and find delivery personnel with highest total
      const deliveryGuys = tableSummary.filter((item) => item.name !== "total");
      if (deliveryGuys.length === 0) {
        setTopPerformers([]);
        return;
      }

      // Find the maximum total
      const maxTotal = Math.max(...deliveryGuys.map((item) => item.total));
      // Get all delivery personnel with the maximum total
      const topGuys = deliveryGuys.filter((item) => item.total === maxTotal);

      // Fetch user info for each top performer
      const topPerformersWithInfo = await Promise.all(
        topGuys.map(async (topGuy) => {
          if (topGuy.id) {
            try {
              const { default: getDocumentById } = await import(
                "../../api/services/Status/getStatus"
              );

              const getUserData = () =>
                new Promise((resolve) => {
                  const unsubscribe = getDocumentById(
                    "deliveryguy",
                    topGuy.id,
                    (userData) => {
                      unsubscribe();
                      resolve(userData);
                    }
                  );
                });

              const userData = await getUserData();
              return {
                ...topGuy,
                userInfo: userData || {},
              };
            } catch (error) {
              console.error(
                `Error fetching user info for ${topGuy.id}:`,
                error
              );
              return {
                ...topGuy,
                userInfo: {},
              };
            }
          }
          return topGuy;
        })
      );

      // Sort by name for consistent display
      const sortedTopPerformers = topPerformersWithInfo.sort((a, b) =>
        a.name.localeCompare(b.name)
      );
      setTopPerformers(sortedTopPerformers);
    };

    findTopPerformers();
  }, [tableSummary]);

  // Fetch best selling items for this 15 days
  useEffect(() => {
    const fetchBestSellingItems = async () => {
      if (!branchId) return;

      setLoading(true);
      try {
        const db = getFirestore();
        const startOf15days = getStartOf15days();
        const orderTypesResponse = await getOrderTypes();
        const orderTypesMap = {};
        orderTypesResponse.forEach((cat) => {
          orderTypesMap[cat.name] = cat.orderTypes;
        });

        // Query all orders from this 15 days
        const q = query(
          collection(db, "allTimeOrders"),
          where("active", "==", active)
        //   where("branchId", "==", branchId),
        //   where("date", ">=", startOf15days)
        );

        const querySnapshot = await getDocs(q);
        const orders = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Count items by type and category
        const itemCounts = {};
        orders.forEach((order) => {
          if (order.type && order.categoryId) {
            const key = `${order.categoryId}-${order.type}`;
            if (!itemCounts[key]) {
              let meas = "";
              if (
                order.categoryId !== "custom" &&
                orderTypesMap[order.categoryId]
              ) {
                const typeObj = orderTypesMap[order.categoryId].find(
                  (t) => t.name === order.type
                );
                if (typeObj) meas = typeObj.measurement || "";
              }
              itemCounts[key] = {
                item: order.type,
                category: order.categoryId,
                count: 0,
                quantity: 0,
                measurement: meas,
              };
            }
            itemCounts[key].count += 1;
            itemCounts[key].quantity += parseInt(order.quantity || 0);
          }
        });

        // Convert to array and sort by count
        const sortedItems = Object.values(itemCounts)
          .sort((a, b) => b.count - a.count)
          .slice(0, 5); // Get top 5

        setBestSellingItems(sortedItems);
      } catch (error) {
        console.error("Error fetching best selling items:", error);
        setBestSellingItems([]);
      } finally {
        setLoading(false);
      }
    };

    fetchBestSellingItems();
  }, [branchId]);

  // Fetch best customers for this 15 days
  useEffect(() => {
    const fetchBestCustomers = async () => {
      if (!branchId || !activeSheet) return;

      setCustomerLoading(true);
      try {
        const db = getFirestore();
        const startOf15days = getStartOf15days();
        const collections = ["Asbeza", "Card", "Water", "Wifi"];
        const customerOrders = {};

        // Query orders from each collection
        for (const collectionName of collections) {
          const q = query(
            collection(db, collectionName),
            // where("branchId", "==", branchId),
            where("active", "==", active),
            // where("date", ">=", startOf15days)
          );

          const querySnapshot = await getDocs(q);
          querySnapshot.forEach((doc) => {
            const order = doc.data();
            if (order.blockHouse) {
              const customerId = generateCustomID(
                order.blockHouse.toUpperCase()
              );
              if (!customerOrders[customerId]) {
                customerOrders[customerId] = {
                  blockHouse: order.blockHouse,
                  Asbeza: 0,
                  Card: 0,
                  Water: 0,
                  Wifi: 0,
                  Total: 0,
                };
              }
              customerOrders[customerId][collectionName]++;
              customerOrders[customerId].Total++;
            }
          });
        }

        // Convert to array and sort by total orders
        const sortedCustomers = Object.entries(customerOrders)
          .map(([id, data]) => ({ customerId: id, ...data }))
          .sort((a, b) => b.Total - a.Total)
          .slice(0, 10); // Get top 10

        // Fetch customer details
        const customerPromises = sortedCustomers.map(async (customer) => {
          const customerDocRef = doc(db, "customer", customer.customerId);
          const customerDoc = await getDoc(customerDocRef);
          const customerData = customerDoc.exists() ? customerDoc.data() : {};
          const membershipExpiry = customerData.membershipExpiry
            ? new Date(customerData.membershipExpiry)
            : null;
          const isMember =
            membershipExpiry && membershipExpiry > new Date() ? "Yes" : "No";
          return {
            ...customer,
            name: customerData.name || "Unknown",
            phone: customerData.phone || "N/A",
            member: isMember,
          };
        });

        const customersWithDetails = await Promise.all(customerPromises);
        setBestCustomers(customersWithDetails);
      } catch (error) {
        console.error("Error fetching best customers:", error);
        setBestCustomers([]);
      } finally {
        setCustomerLoading(false);
      }
    };

    fetchBestCustomers();
  }, [branchId, activeSheet]);

  return (
    <Box sx={{ padding: 3 }}>
      {/* Top Performers Section */}
      {topPerformers.length > 0 ? (
        <Paper
          elevation={8}
          sx={{
            padding: 4,
            margin: "2rem auto",
            maxWidth: "600px",
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            borderRadius: 3,
            textAlign: "center",
            boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
          }}
        >
          <Typography
            variant="h4"
            color="white"
            fontWeight="bold"
            sx={{ mb: 2 }}
          >
            🏆 SHEET TOP PERFORMER{topPerformers.length > 1 ? "S" : ""} 🏆
          </Typography>

          {topPerformers.map((performer, index) => (
            <Box key={performer.id || index} sx={{ mb: 4 }}>
              <Typography
                variant="h1"
                color="white"
                fontWeight="bold"
                sx={{
                  mb: 2,
                  fontSize: { xs: "2rem", md: "3rem" },
                  textShadow: "2px 2px 4px rgba(0,0,0,0.5)",
                }}
              >
                {performer.name}
              </Typography>

              {/* Display user avatar and info if available */}
              {performer.userInfo && (
                <Box sx={{ mb: 3 }}>
                  <Avatar
                    sx={{
                      bgcolor: theme.palette.background.alt,
                      color: "white",
                      cursor: "pointer",
                      width: avatarSize,
                      height: avatarSize,
                      mx: "auto",
                      mb: 2,
                    }}
                    src={performer.userInfo.profileImage}
                    aria-label="delivery guy image"
                  >
                    {performer.userInfo.fullName?.charAt(0).toUpperCase() ||
                      performer.name.charAt(0).toUpperCase()}
                  </Avatar>

                  {performer.userInfo.phone && (
                    <Typography
                      variant="h4"
                      color="white"
                      sx={{
                        opacity: 0.9,
                        textShadow: "1px 1px 2px rgba(0,0,0,0.5)",
                      }}
                    >
                      📞 {performer.userInfo.phone}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          ))}
        </Paper>
      ) : (
        <Paper
          elevation={3}
          sx={{
            padding: 3,
            margin: "2rem auto",
            maxWidth: "400px",
            backgroundColor: theme.palette.background.alt,
            textAlign: "center",
          }}
        >
          <Typography variant="h5" color={theme.palette.secondary[100]}>
            No data available
          </Typography>
        </Paper>
      )}

      {/* Best Selling Items Section */}
      <Paper
        elevation={6}
        sx={{
          padding: 3,
          margin: "2rem auto",
          maxWidth: "800px",
          backgroundColor: theme.palette.background.alt,
          borderRadius: 2,
        }}
      >
        <Typography
          variant="h4"
          color={theme.palette.secondary[100]}
          fontWeight="bold"
          sx={{ mb: 3, textAlign: "center" }}
        >
          🛍️ Sheet Best Selling Items
        </Typography>

        {loading ? (
          <Typography
            variant="h6"
            color={theme.palette.secondary[200]}
            sx={{ textAlign: "center" }}
          >
            Loading...
          </Typography>
        ) : bestSellingItems.length > 0 ? (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow
                  sx={{ backgroundColor: theme.palette.background.alt }}
                >
                  <TableCell
                    sx={{
                      color: theme.palette.secondary[50],
                      fontWeight: "bold",
                      fontSize: "1.1rem",
                      borderBottom: `1px solid ${
                        theme.palette.mode === "dark" ? "#323E8B" : "#E4E6F2"
                      }`,
                    }}
                  >
                    Item
                  </TableCell>
                  <TableCell
                    sx={{
                      color: theme.palette.secondary[50],
                      fontWeight: "bold",
                      fontSize: "1.1rem",
                      borderBottom: `1px solid ${
                        theme.palette.mode === "dark" ? "#323E8B" : "#E4E6F2"
                      }`,
                    }}
                  >
                    Category
                  </TableCell>
                  <TableCell
                    sx={{
                      color: theme.palette.secondary[50],
                      fontWeight: "bold",
                      fontSize: "1.1rem",
                      borderBottom: `1px solid ${
                        theme.palette.mode === "dark" ? "#323E8B" : "#E4E6F2"
                      }`,
                    }}
                  >
                    Orders (Units)
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bestSellingItems.map((item, index) => (
                  <TableRow key={`${item.item}-${item.category}`}>
                    <TableCell
                      sx={{
                        color: theme.palette.secondary[50],
                        fontSize: "1rem",
                        borderBottom: `1px solid ${
                          theme.palette.mode === "dark" ? "#323E8B" : "#E4E6F2"
                        }`,
                      }}
                    >
                      {index + 1}. {item.item}{" "}
                      {item.measurement && `(${item.measurement})`}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: theme.palette.secondary[200],
                        fontSize: "1rem",
                        borderBottom: `1px solid ${
                          theme.palette.mode === "dark" ? "#323E8B" : "#E4E6F2"
                        }`,
                      }}
                    >
                      {item.category}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: theme.palette.secondary[50],
                        fontWeight: "bold",
                        fontSize: "1.1rem",
                        borderBottom: `1px solid ${
                          theme.palette.mode === "dark" ? "#323E8B" : "#E4E6F2"
                        }`,
                      }}
                    >
                      {item.count}
                      {item.category !== "custom" ? ` (${item.quantity})` : ""}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography
            variant="h6"
            color={theme.palette.secondary[200]}
            sx={{ textAlign: "center" }}
          >
            No orders found for this 15 days
          </Typography>
        )}
      </Paper>

      {/* Best Customers Section */}
      <Paper
        elevation={6}
        sx={{
          padding: 3,
          margin: "2rem auto",
          maxWidth: "1000px",
          backgroundColor: theme.palette.background.alt,
          borderRadius: 2,
        }}
      >
        <Typography
          variant="h4"
          color={theme.palette.secondary[100]}
          fontWeight="bold"
          sx={{ mb: 3, textAlign: "center" }}
        >
          👥 Sheet Best Customers
        </Typography>

        {customerLoading ? (
          <Typography
            variant="h6"
            color={theme.palette.secondary[200]}
            sx={{ textAlign: "center" }}
          >
            Loading...
          </Typography>
        ) : bestCustomers.length > 0 ? (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow
                  sx={{ backgroundColor: theme.palette.background.alt }}
                >
                  {[
                    "Name",
                    "Phone",
                    "Block House",
                    "Member",
                    "Asbeza",
                    "Card",
                    "Water",
                    "Wifi",
                    "Total",
                  ].map((header) => (
                    <TableCell
                      key={header}
                      sx={{
                        color: theme.palette.secondary[50],
                        fontWeight: "bold",
                        fontSize: "1.1rem",
                        borderBottom: `1px solid ${
                          theme.palette.mode === "dark" ? "#323E8B" : "#E4E6F2"
                        }`,
                      }}
                    >
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {bestCustomers.map((customer, index) => (
                  <TableRow key={customer.customerId}>
                    <TableCell
                      sx={{
                        color: theme.palette.secondary[50],
                        fontSize: "1rem",
                        borderBottom: `1px solid ${
                          theme.palette.mode === "dark" ? "#323E8B" : "#E4E6F2"
                        }`,
                      }}
                    >
                      {index + 1}. {customer.name}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: theme.palette.secondary[200],
                        fontSize: "1rem",
                        borderBottom: `1px solid ${
                          theme.palette.mode === "dark" ? "#323E8B" : "#E4E6F2"
                        }`,
                      }}
                    >
                      {customer.phone}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: theme.palette.secondary[200],
                        fontSize: "1rem",
                        borderBottom: `1px solid ${
                          theme.palette.mode === "dark" ? "#323E8B" : "#E4E6F2"
                        }`,
                      }}
                    >
                      {customer.blockHouse}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: theme.palette.secondary[200],
                        fontSize: "1rem",
                        borderBottom: `1px solid ${
                          theme.palette.mode === "dark" ? "#323E8B" : "#E4E6F2"
                        }`,
                      }}
                    >
                      {customer.member}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: theme.palette.secondary[50],
                        fontWeight: "bold",
                        fontSize: "1.1rem",
                        borderBottom: `1px solid ${
                          theme.palette.mode === "dark" ? "#323E8B" : "#E4E6F2"
                        }`,
                      }}
                    >
                      {customer.Asbeza}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: theme.palette.secondary[50],
                        fontWeight: "bold",
                        fontSize: "1.1rem",
                        borderBottom: `1px solid ${
                          theme.palette.mode === "dark" ? "#323E8B" : "#E4E6F2"
                        }`,
                      }}
                    >
                      {customer.Card}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: theme.palette.secondary[50],
                        fontWeight: "bold",
                        fontSize: "1.1rem",
                        borderBottom: `1px solid ${
                          theme.palette.mode === "dark" ? "#323E8B" : "#E4E6F2"
                        }`,
                      }}
                    >
                      {customer.Water}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: theme.palette.secondary[50],
                        fontWeight: "bold",
                        fontSize: "1.1rem",
                        borderBottom: `1px solid ${
                          theme.palette.mode === "dark" ? "#323E8B" : "#E4E6F2"
                        }`,
                      }}
                    >
                      {customer.Wifi}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: theme.palette.secondary[50],
                        fontWeight: "bold",
                        fontSize: "1.1rem",
                        borderBottom: `1px solid ${
                          theme.palette.mode === "dark" ? "#323E8B" : "#E4E6F2"
                        }`,
                      }}
                    >
                      {customer.Total}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography
            variant="h6"
            color={theme.palette.secondary[200]}
            sx={{ textAlign: "center" }}
          >
            No customer orders found for this 15 days
          </Typography>
        )}
      </Paper>
    </Box>
  );
};

export default DataAnalysis;
